<script setup lang="ts">
  import { useEntityCard } from '@/composables/useEntityCard'
  import { useTranslation } from '@/composables/useTranslation'
  import { entitiesDE } from '@/i18n/entity'
  import type { CustomerCase } from '@/types/generated'
  import { EntityTypeEnum } from '@/types/generated'
  import { BuildingIcon, ChevronLeft, UserIcon } from 'lucide-vue-next'
  import { computed, inject, ref, type Ref } from 'vue'

  const { t } = useTranslation({
    en: {
      Participants: 'Parties',
      Subtitle: 'AI chat has access to all data in the case.',
      UnknownRole: 'Unknown Role'
    },
    de: {
      Participants: 'Beteiligte',
      Subtitle: 'Der KI-Chat hat Zugriff auf alle Daten in diesem Fall.',
      UnknownRole: 'Unbekannte Rolle'
    }
  })

  const caseData = inject<Ref<CustomerCase | null>>('caseData', ref(null))
  const { showCard } = useEntityCard()

  const formatParticipantName = (entity: any): string => {
    if (entity.entity_type === EntityTypeEnum.NATURAL_PERSON) {
      return `${entity.first_name ?? ''} ${entity.last_name ?? ''}`.trim()
    }
    return `${entity.company_name ?? ''} ${entity.company_legal_form ?? ''}`.trim()
  }

  const participants = computed(() =>
    (caseData.value?.data?.entities || []).filter((e: any) => !!formatParticipantName(e.entity))
  )

  const emit = defineEmits<{ back: []; close: [] }>()

  const handleParticipantClick = (entity: any, element: HTMLElement) => {
    showCard({ ...entity, name: formatParticipantName(entity) }, element)
  }
</script>

<template>
  <div class="flex flex-col">
    <!-- Header -->
    <div class="relative flex items-center border-b border-gray-100 px-4 py-3">
      <button
        type="button"
        class="shrink-0 text-gray-500 hover:text-gray-700"
        @click="emit('back')"
      >
        <ChevronLeft class="size-5" />
      </button>
      <div class="absolute left-1/2 flex -translate-x-1/2 items-center gap-2">
        <span class="text-sm font-semibold text-gray-800">{{ t('Participants') }}</span>
        <span
          v-if="participants.length"
          class="rounded-full bg-gray-100 px-1.5 py-0.5 text-[10px] font-semibold text-gray-800"
        >
          {{ participants.length }}
        </span>
      </div>
    </div>
    <p class="px-4 pb-1 pt-2 text-center text-xs text-gray-500">{{ t('Subtitle') }}</p>
    <!-- Participants list -->
    <div class="max-h-[380px] overflow-y-auto px-4 py-2">
      <div
        v-for="entity in participants"
        :key="entity.entity.uuid"
        class="flex cursor-pointer items-center gap-3 rounded-md px-2 py-2 hover:bg-gray-50"
        @click="handleParticipantClick(entity.entity, $event.currentTarget as HTMLElement)"
      >
        <component
          :is="
            entity.entity.entity_type === EntityTypeEnum.NATURAL_PERSON ? UserIcon : BuildingIcon
          "
          class="size-5 shrink-0 text-gray-500"
        />
        <div class="min-w-0 flex-1">
          <p class="truncate text-xs text-gray-500">
            {{ entitiesDE[entity.relationships[0]?.relationship_type] || t('UnknownRole') }}
          </p>
          <p class="truncate text-sm font-medium text-gray-800">
            {{ formatParticipantName(entity.entity) }}
          </p>
        </div>
      </div>
    </div>
  </div>
</template>
